'use client'

import {
  Calendar,
  Pen as PenTool,
  CheckboxChecked as CheckSquare,
  Time as Clock,
  Group as Users,
  Rocket,
  type CarbonIconType,
} from '@carbon/icons-react'

import {
  EASE,
  HAIRLINE,
  QuietLink,
  SectionHead,
  useReveal,
} from '@/components/page-kit'
import { cn } from '@/lib/utils'

/* Three steps as one ruled row — the navbar-cell grammar — instead of three
   glowing slate cards on a hard slate-900 band. The step number is the cell's
   device: outsize, mono, sitting behind nothing. Tokens only, both themes. */

const steps: {
  num: string
  title: string
  desc: string
  icon: CarbonIconType
  metrics: { value: string; icon: CarbonIconType }[]
}[] = [
  {
    num: '01',
    title: 'Discovery Call',
    desc: "A 30-minute conversation with our implementation team. We understand your school's size, board, pain points, and current tools to create a tailored plan.",
    icon: Calendar,
    metrics: [
      { value: '30 min', icon: Clock },
      { value: 'Dedicated', icon: Users },
      { value: 'Custom plan', icon: Rocket },
    ],
  },
  {
    num: '02',
    title: 'Configuration',
    desc: 'Our team sets up your fee structures, grade sections, timetable, staff records, and parent accounts tailored to how your school actually runs.',
    icon: PenTool,
    metrics: [
      { value: '1-2 weeks', icon: Clock },
      { value: 'Dedicated', icon: Users },
      { value: 'Ready setup', icon: Rocket },
    ],
  },
  {
    num: '03',
    title: 'Go Live',
    desc: 'Your staff is trained, your data migrated, and your parents onboarded. Dedicated support for the first 30 days and beyond.',
    icon: CheckSquare,
    metrics: [
      { value: 'Same day', icon: Clock },
      { value: 'Dedicated', icon: Users },
      { value: 'Live system', icon: Rocket },
    ],
  },
]

function StepCell({
  step,
  index,
  shown,
}: {
  step: (typeof steps)[number]
  index: number
  shown: boolean
}) {
  const Icon = step.icon

  return (
    <div
      className="flex h-full flex-col bg-background p-6 sm:p-7 md:p-8"
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.6s ${EASE} ${index * 120}ms, transform 0.6s ${EASE} ${index * 120}ms`,
      }}
    >
      <div className="flex items-start justify-between">
        <Icon
          aria-hidden
          size={24}
          className="text-emerald-600 dark:text-primary-active"
        />
        <span className="font-mono text-4xl leading-none text-muted-foreground/25 md:text-5xl">
          {step.num}
        </span>
      </div>

      <h3 className="font-heading mt-6 text-xl font-normal text-foreground md:text-2xl">
        {step.title}
      </h3>

      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
        {step.desc}
      </p>

      {/* Record line: the same three fields per step, set as quiet mono text
          rather than pill chips. */}
      <div
        className={cn(
          'mt-auto flex flex-wrap gap-x-5 gap-y-2 border-t pt-4',
          HAIRLINE,
          'mt-6'
        )}
      >
        {step.metrics.map(({ value, icon: MetricIcon }) => (
          <span
            key={value}
            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide text-muted-foreground"
          >
            <MetricIcon
              aria-hidden
              size={12}
              className="text-emerald-600/70 dark:text-primary-active/70"
            />
            {value}
          </span>
        ))}
      </div>
    </div>
  )
}

export function HowItWorks() {
  const { ref, shown } = useReveal<HTMLDivElement>(0.15)

  return (
    <section className="bg-background py-16 sm:py-20 md:py-28">
      <div className="container mx-auto">
        <SectionHead
          eyebrow="Simple Setup"
          title="Launch Faster With a Streamlined Implementation Process"
          deck="A smooth, guided setup tailored to your school’s workflow and operations"
          action={
            <QuietLink href="/implementation">
              See the full implementation process
            </QuietLink>
          }
        />

        <div
          ref={ref}
          className={cn(
            'mt-10 overflow-hidden rounded-xl border sm:mt-12',
            HAIRLINE
          )}
        >
          <div className="grid gap-px bg-border/70 md:grid-cols-3">
            {steps.map((step, idx) => (
              <StepCell key={step.num} step={step} index={idx} shown={shown} />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
